import ClaudeService from "./claudeService";
import { resolveIslandModelId } from "../lib/worldLayout";

export async function parseBrainDump(messages, existingWorld = null) {
  // For the merge prompt, strip surface_items so Claude only sees the
  // district/island/building/room hierarchy. Full document content in
  // surface_items would consume the token budget and risk truncation.
  // Existing items are merged back in code after Claude returns.
  const worldForPrompt = existingWorld?.districts?.length
    ? { ...existingWorld, districts: withoutLayoutFields(existingWorld.districts), surface_items: [] }
    : existingWorld;

  const systemPrompt = existingWorld?.districts?.length
    ? `You are Lumen, the AI inside NeuroQuest. The user already has a world. MERGE their new brain dump into the existing world. Add new content - do not remove or rename anything that already exists.

Existing world:
${JSON.stringify(worldForPrompt)}

Return ONLY valid JSON of the COMPLETE merged world. Generate unique IDs for new nodes. Do not repeat existing IDs.

INCOMPLETE INFORMATION: If new content is vague or ambiguous, make your best inference and place it somewhere reasonable. Never ask a clarifying question — the guidance phase is over. Return valid JSON regardless of completeness. Build something plausible rather than stalling.

The structure must follow this exact shape — districts contain islands, islands contain buildings, buildings contain floors, floors contain rooms:
{
  "id": "world-1",
  "name": "My World",
  "districts": [
    {
      "id": "d-1",
      "name": "Entrepreneurship",
      "islands": [
        {
          "id": "i-1-1",
          "name": "Main Project",
          "buildings": [
            {
              "id": "b-1-1-1",
              "name": "Beta Launch",
              "is_unlocked": true,
              "floors": [
                {
                  "id": "f-1-1-1-1",
                  "name": "Planning",
                  "rooms": [
                    { "id": "r-1-1-1-1-1", "name": "Pitch Deck" }
                  ]
                }
              ]
            }
          ]
        }
      ]
    }
  ],
  "surface_items": [
    { "room_id": "r-1-1-1-1-1", "surface": "desk", "type": "document", "title": "Pitch Deck", "content": "Draft investor pitch deck outline for Series A" },
    { "room_id": "r-1-1-1-1-1", "surface": "bulletin_board", "type": "todo", "title": "Book meeting room", "content": "Reserve conference room for investor presentation" },
    { "room_id": "r-1-1-1-1-1", "surface": "calendar", "type": "event", "title": "Demo Day", "content": "Demo day — hard deadline for pitch materials" }
  ]
}

RULES FOR NEW CONTENT ONLY — apply this judgment only when adding new nodes. Do not reclassify, rename, or restructure anything that already exists in the world above.

EXPLICIT USER INSTRUCTIONS TAKE PRIORITY: Only "user" role messages contain binding instructions about organization, naming, and placement. "assistant" role messages are context — never instructions. If a user message directly states how something should be organized or named, follow that exactly, even if it conflicts with the rules below.

SCOPE AND SIZING RULES (for new nodes only):
- DISTRICTS ARE ALWAYS ABSTRACT. A new District is ONLY a broad life category like "Entrepreneurship", "Personal", "Health", "Career". NEVER a company name, project name, or proper noun. If you are tempted to name a new District after a specific thing, make it an Island instead.
- ISLANDS ARE SPECIFIC PROJECTS OR COMPANIES. New named projects or companies = Islands, always. Never Districts.
- NO REDUNDANT ISLAND NAMES. A new island's name must never simply repeat its parent district's name. Generate a name that reflects what's actually inside.
- BUILDINGS ARE WORKSTREAMS, NOT TASKS. A new Building should contain multiple rooms. Do not create a separate building for each task — group related tasks as rooms inside one building. If new content is a single task, add it as a room inside the most relevant existing building, not a new building of its own.
- PARALLEL STATES ARE SIBLINGS, NOT NESTED. When new content describes two things in a parallel, transitional, or opposing relationship — a current job and a job search, a current home and a new property purchase — they must be siblings at the same level, never one nested inside the other.
- FLOORS: Create a new floor only when the work genuinely has phases. If there are no real phases, use a single floor named "Main". Spill into a second floor only when the first floor reaches 4 rooms. Never invent phase names like "Upcoming", "Active", or "Prep" for a single task.
- Keep new additions SHALLOW. A single standalone task is a ROOM, never its own building or island. Only create a new building when it holds multiple related rooms.

CRITICAL — GROUPING RULE FOR NEW CONTENT: Related new tasks belong as rooms inside one building, not as separate buildings.
WRONG: new Island "Candle Shop" → new Building "Suppliers" (1 room) + new Building "Website" (1 room) + new Building "Instagram" (1 room)
RIGHT: new Island "Candle Shop" → new Building "Launch" → Floor "Main" → Rooms: "Suppliers", "Website", "Instagram"

ANTI-FRAGMENTATION CHECK (for new nodes): Before finalizing buildings for a new island, check whether you have multiple buildings that each have only 1 floor and fewer than 3 rooms, and whose workstreams all serve the same overall goal (e.g. launching one product or business). If so, consolidate them into a single building. Use the workstreams as rooms, grouped under sensible floor names if real phases exist, or one "Main" floor if not. Keep buildings separate only when they represent a genuinely distinct phase or type of effort with real independent complexity.
WRONG: new Island "Candle Shop" → new Building "Suppliers" (1 floor, 2 rooms) + new Building "Website" (1 floor, 3 rooms) + new Building "Marketing" (1 floor, 2 rooms) + new Building "Legal & Admin" (1 floor, 2 rooms)
RIGHT: new Island "Candle Shop" → new Building "Launch" → Floor "Main" → Rooms: Supplier Research, Materials List, Platform Setup, Product Listings, Launch Checklist, Instagram Setup, Content Plan, Business Licensing, Pricing & Margins
Exception: A Craft Fair with its own Prep and Day-Of floors stays separate — it is a distinct event, not part of the core launch workstream.

NODE COUNT LIMITS (for new nodes): Maximum 5 districts total, 4 islands per district, 5 buildings per island, 2 floors per building, 4 rooms per floor.

is_unlocked FOR NEW NODES: New buildings and rooms on the first island (index 0 in the first district) inherit is_unlocked: true. New buildings and rooms on all other islands get is_unlocked: false.

surface_items: ONLY output NEW items from this brain dump. Existing surface_items are not included in the input above and will be restored separately in code — do not attempt to reproduce them. Only classify content from the new brain dump.

Surface types and when to use each:
- "whiteboard" — decisions made, open questions, findings, ideas
  types: "decision" (confirmed), "finding" (known fact), "question" (unresolved), "idea" (option not yet decided)
- "bulletin_board" — concrete next actions; type: "todo"
- "calendar" — dates, deadlines, scheduled events; type: "event"
- "desk" — content that needs drafting or writing; type: "document"
- "bookshelf" — links, resources, references; type: "reference"

Each item: { "room_id": "<room id from merged tree>", "surface": "...", "type": "...", "title": "Short label", "content": "Specific content from the brain dump" }

Rules:
- room_id must reference a room in the merged districts tree
- Hard deadlines → calendar event. Open questions → whiteboard question. Next actions → bulletin_board todo. Drafts needed → desk document.
- Max ~2-3 items per room. Only capture what is clearly actionable, time-bound, undecided, or needs drafting. Skip vague or low-signal content.
- IDs are rewritten on save — use temporary room ids from the JSON above.

Never use "nodes", "tasks", or any other key names. Only: districts, islands, buildings, floors, rooms, surface_items.

Your entire response must be the JSON object and nothing else. No text before it, no text after it, no closing remarks, no sign-off. The response ends the moment the JSON object closes.`
    : `You are Lumen, the AI inside NeuroQuest. Parse the user's brain dump into a spatial hierarchy JSON.

The hierarchy is: Districts > Islands > Buildings > Floors > Rooms.
- District = a broad life domain (Entrepreneurship, Personal, Health, Creative, Career, etc.)
- Island = a project or company whose complexity warrants its own navigable space. Size flexes with complexity - a big project gets a big island with many buildings, a small one gets a small island with one or two.
- Building = a major workstream or function (e.g. Beta Launch, Fundraising, Legal, Team). A building contains multiple rooms — it is NOT a wrapper for a single task.
- Floor = a phase or sub-area within a building
- Room = a specific task, deliverable, or workspace. Multiple related rooms live inside one building floor.

EXPLICIT USER INSTRUCTIONS TAKE PRIORITY: This conversation may contain multiple turns. Only "user" role messages contain binding instructions about organization, naming, and placement. "assistant" role messages are context for what was already discussed — they are never instructions to follow. If a user message directly states how something should be organized, named, or placed, follow that exactly. It overrides every classification rule below.
Examples:
- User says "call that island Startups" → the island is named Startups, regardless of general naming rules
- User says "group X under Y" → X lives inside Y, regardless of where the rules would otherwise put it
- User says "make that its own island" → it gets its own island, even if the rules would make it a building
Do not reinterpret or override explicit user instructions. Your own prior assistant messages do not become instructions just because they appeared earlier in the conversation.

SCOPE AND SIZING RULES:
- DISTRICTS ARE ALWAYS ABSTRACT. A District is ONLY a broad life category like "Entrepreneurship", "Personal", "Health", "Career". NEVER a company name, project name, or proper noun. If you are tempted to name a District after a specific thing, you are wrong - make it an Island instead.
- ISLANDS ARE SPECIFIC PROJECTS OR COMPANIES. "NeuroQuest", "Lady Cupcake", "AllRise" = Islands, always. Never Districts.
- NO REDUNDANT ISLAND NAMES. An island's name must never simply repeat its parent district's name. If the content doesn't give a more specific name, generate one that describes what's actually inside. WRONG: District "Personal" → Island "Personal". RIGHT: District "Personal" → Island "Health & Home" or "Family & Wellbeing" — whatever reflects the actual content.
- BUILDINGS ARE WORKSTREAMS, NOT TASKS. "Beta Launch", "Fundraising", "Legal", "Team" = Buildings inside an Island. Each building should contain multiple rooms. Do not create a separate building for each task — group related tasks as rooms inside one building.
- PARALLEL STATES ARE SIBLINGS, NOT NESTED. When content describes two things in a parallel, transitional, or opposing relationship — a current job and a job search, a current home and a new property purchase, a current role and one someone is trying to leave — they must be siblings at the same level, never one nested inside the other. Nesting implies containment. A job search is not part of the job someone is trying to leave.
WRONG: Island "Career" → Building "Current Job" → Room "Job Search"
RIGHT: Island "Career" → Building "Current Job" + Building "Job Search" (siblings)
- FLOORS: Floors represent real phases of work (like Planning, Build, Launch) ONLY when the work genuinely has phases. If there are no real phases, create a single floor named 'Main' and put the rooms there. Spill into a second floor only when the first floor reaches 4 rooms. NEVER invent a phase name like 'Upcoming', 'Active', or 'Prep' for a single task or a simple building. A floor is either a real phase or just a container, never a decorative label.
- Let complexity determine island size. A big project gets many buildings. A small one gets one or two.
- Multiple projects in same domain = same District, separate Islands.
- Small side projects (a bakery, a small product) = a Building or small Island inside the relevant District
- Multiple companies or projects in same domain → same District, separate Islands
- The world should feel proportional - big complex projects get more geography, simple ones get less
- Example: A software startup → District "Entrepreneurship" → Island "Startup Name" → Buildings "Product", "Fundraising", "Legal"
- Example: A small side business → District "Entrepreneurship" → Island "Side Business" (small, 1-2 buildings)
- Example: A fitness goal → District "Health" → Island "Fitness" → Building "Training"
- Example: A quick task with no project context → tuck it into the most relevant existing building

CRITICAL — GROUPING RULE: Related tasks belong as rooms inside one building, not as separate buildings.
WRONG (never do this):
  Island "NeuroQuest" → Building "Pitch Deck" (1 room) + Building "Feature Scope" (1 room) + Building "Investor List" (1 room)
RIGHT (always do this):
  Island "NeuroQuest" → Building "Fundraising" → Floor "Main" → Rooms: "Pitch Deck", "Feature Scope", "Investor List"

ANTI-FRAGMENTATION CHECK: Before finalizing buildings for an island, check whether you have multiple buildings that each have only 1 floor and fewer than 3 rooms, and whose workstreams all serve the same overall goal (e.g. launching one product or business). If so, consolidate them into a single building. Use the workstreams as rooms, grouped under sensible floor names if real phases exist, or one "Main" floor if not. Keep buildings separate only when they represent a genuinely distinct phase or type of effort with real independent complexity.
WRONG: Island "Candle Shop" → Building "Suppliers" (1 floor, 2 rooms) + Building "Website" (1 floor, 3 rooms) + Building "Marketing" (1 floor, 2 rooms) + Building "Legal & Admin" (1 floor, 2 rooms)
RIGHT: Island "Candle Shop" → Building "Launch" → Floor "Main" → Rooms: Supplier Research, Materials List, Platform Setup, Product Listings, Launch Checklist, Instagram Setup, Content Plan, Business Licensing, Pricing & Margins
Exception: A Craft Fair with its own Prep and Day-Of floors stays separate — it is a distinct event, not part of the core launch workstream.

Keep worlds SHALLOW. A single standalone task is a ROOM, never its own building or island. Only create a building when it holds multiple related rooms. Things like 'do my taxes' or 'read more' are single rooms inside a broad relevant building, not buildings of their own. Prefer fewer, fuller buildings. When unsure, make it a room.

Return ONLY valid JSON, no other text, in this exact structure:
{
  "id": "world-1",
  "name": "My World",
  "districts": [
    {
      "id": "d-1",
      "name": "Entrepreneurship",
      "islands": [
        {
          "id": "i-1-1",
          "name": "Main Project",
          "buildings": [
            {
              "id": "b-1-1-1",
              "name": "Fundraising",
              "is_unlocked": true,
              "floors": [
                {
                  "id": "f-1-1-1-1",
                  "name": "Main",
                  "rooms": [
                    { "id": "r-1-1-1-1-1", "name": "Pitch Deck" },
                    { "id": "r-1-1-1-1-2", "name": "Feature Scope" },
                    { "id": "r-1-1-1-1-3", "name": "Investor List" }
                  ]
                }
              ]
            }
          ]
        }
      ]
    }
  ],
  "surface_items": [
    { "room_id": "r-1-1-1-1-1", "surface": "desk", "type": "document", "title": "Pitch Deck", "content": "Draft of investor pitch deck for Series A" },
    { "room_id": "r-1-1-1-1-2", "surface": "bulletin_board", "type": "todo", "title": "Define feature scope", "content": "Outline core features for investor demo" },
    { "room_id": "r-1-1-1-1-3", "surface": "whiteboard", "type": "question", "title": "Investor targets", "content": "Which investors to approach first — warm intros vs cold outreach?" },
    { "room_id": "r-1-1-1-1-1", "surface": "calendar", "type": "event", "title": "Demo Day", "content": "Target demo day — 8 weeks from now" }
  ]
}

Rules:
- Use simple readable names, no jargon
- If something does not clearly fit a level, use your best judgment
- Minimum 1 district, 1 island, 1 building, 1 floor, 1 room
- Maximum 5 districts, 4 islands per district, 5 buildings per island, 2 floors per building, 4 rooms per floor
- Be concise with names - short and clear, no long descriptions
- Consolidate aggressively - fewer nodes is better than more
- IDs must be unique strings like "d-1", "i-1-1", "b-1-1-1", "f-1-1-1-1", "r-1-1-1-1-1" (room IDs are rewritten on save)
- Each building must include "is_unlocked": on the FIRST island only, set true on every building; on all other islands set false on every building (only the first work island is accessible at start)
- Each room must include "is_unlocked" matching its building: true on every room of the first island, false on all other islands (unlocked building must never have all rooms locked)
- surface_items: classify content from the brain dump onto the correct room surface based on type.
  Surface types: "whiteboard" (types: decision, finding, question, idea), "bulletin_board" (type: todo), "calendar" (type: event), "desk" (type: document), "bookshelf" (type: reference).
  Each item: { "room_id": "<room id from hierarchy>", "surface": "...", "type": "...", "title": "Short label", "content": "Specific content from the brain dump" }
  Hard deadlines → calendar event. Open questions → whiteboard question. Next actions → bulletin_board todo. Drafts needed → desk document.
  Max ~2-3 items per room. Only capture what is clearly actionable, time-bound, undecided, or needs drafting. Skip vague or low-signal content.

INCOMPLETE INFORMATION: If the brain dump is vague, ambiguous, or missing detail, use your best judgment to place content and generate reasonable placeholder names. Never ask a clarifying question in this response — the guidance phase is over. Return valid JSON regardless of how complete or incomplete the input is. Build something plausible rather than stalling.

Your entire response must be the JSON object and nothing else. No text before it, no text after it, no closing remarks, no sign-off. The response ends the moment the JSON object closes.`;

  const result = await ClaudeService.sendMessageWithRetry({
    model: "claude-sonnet-4-6",
    maxTokens: 8192,
    system: systemPrompt,
    messages,
  });

  if (!result.success) {
    throw new Error(result.error || "Brain dump parsing failed");
  }

  // Capture originals before merging so the merge-back has them.
  const originalSurfaceItems = existingWorld?.surface_items || [];

  const raw = result.content || "";
  const clean = ClaudeService._repairLiteralNewlines(raw.replace(/```json|```/g, "").trim());

  // Merge Claude's new items back with the untouched originals.
  // Dedup by room_id+surface+title so Claude can't re-add existing items.
  const mergeBack = (world) => {
    if (!originalSurfaceItems.length) return world;
    const newItems = (world?.surface_items || []).filter(n =>
      !originalSurfaceItems.some(o =>
        o.room_id === n.room_id && o.surface === n.surface && o.title === n.title
      )
    );
    return { ...world, surface_items: [...originalSurfaceItems, ...newItems] };
  };

  try {
    return mergeBack(normalizeTree(JSON.parse(clean)));
  } catch (e) {
    // Salvage truncated JSON
    const closers = [']}]}]}', ']}]}', ']}', '}'];
    for (const closer of closers) {
      try { return mergeBack(normalizeTree(JSON.parse(clean + closer))); } catch {}
    }

    // Try trimming to last complete object
    const lastBrace = clean.lastIndexOf('},');
    if (lastBrace > 100) {
      const trimmed = clean.slice(0, lastBrace + 1);
      for (const closer of closers) {
        try { return mergeBack(normalizeTree(JSON.parse(trimmed + closer))); } catch {}
      }
    }
    throw new Error("JSON parse failed");
  }
}

function withoutLayoutFields(districts) {
  const omit = (node, keys) => {
    const copy = { ...node };
    for (const key of keys) delete copy[key];
    return copy;
  };
  return (districts || []).map((district) => ({
    ...omit(district, ["layout", "color"]),
    islands: (district.islands || []).map((island) => ({
      ...omit(island, ["layout", "island_id", "map_x", "map_y"]),
      buildings: (island.buildings || []).map((building) => omit(building, ["model_id", "colors"])),
    })),
  }));
}

export function normalizeTree(world) {
  if (!world || !Array.isArray(world.districts)) return world;
  world.districts = world.districts.filter(Boolean).map((d, di) => {
    d.id = d.id || `d-${di + 1}`;
    d.islands = (d.islands || []).filter(Boolean).map((isl, ii) => {
      isl.id = isl.id || `i-${di + 1}-${ii + 1}`;
      isl.buildings = (isl.buildings || []).filter(Boolean).map((b, bi) => {
        b.id = b.id || `b-${di + 1}-${ii + 1}-${bi + 1}`;
        b.floors = (b.floors || []).filter(Boolean).map((f, fi) => {
          f.id = f.id || `f-${di + 1}-${ii + 1}-${bi + 1}-${fi + 1}`;
          f.rooms = (f.rooms || []).filter(Boolean).map((r, ri) => {
            r.id = r.id || `r-${di + 1}-${ii + 1}-${bi + 1}-${fi + 1}-${ri + 1}`;
            return r;
          });
          return f;
        });
        return b;
      });
      isl.island_id = resolveIslandModelId(isl);
      return isl;
    });
    return d;
  });
  return world;
}
